import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import { useDeskId } from '@/hooks/useDeskId';
import { useIntegrations } from '@/hooks/useIntegrations';
import { useLiveChatConnection } from '@/hooks/useLiveChatConnection';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectIntegrationId } from '@/redux/session/selectors';
import { LiveChatIntegration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import ConnectCard from './ConnectCard';
import ConnectError from './ConnectError';
import GroupCard from './GroupCard';
import ProfileCard from './ProfileCard';
import SettingUpCard from './SettingUpCard';
import WelcomeTriggerCard from './WelcomeTriggerCard';
import { DeleteFieldset } from '../Components/DeleteFieldset';
import IntegrationConfigWrapper from '../Components/Integration';
import { SessionTimeoutCard } from '../Components/SessionTimeoutCard';

// 1 day — matches the orchestrator's default `livechat` session timeout.
const DEFAULT_SESSION_TIMEOUT = 86400;

const LiveChat = () => {
  const [searchParams] = useSearchParams();
  const integrationId = useSelector(selectIntegrationId);
  const deskId = useDeskId();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const { integration } = useIntegrations<LiveChatIntegration>(deskId, integrationId);
  const connection = useLiveChatConnection(deskId, integrationId);

  if (!integration) {
    return null;
  }

  if (connection !== 'connected') {
    const errorCode = searchParams.get('error');

    return (
      <div className="flex w-xl flex-col gap-4">
        {connection === 'setting_up' ? (
          // No card saves here: a save sends the whole config and would drop the bot that integrations-controller creates.
          <SettingUpCard integration={integration} />
        ) : (
          <>
            {errorCode && <ConnectError code={errorCode} />}
            <fieldset disabled={!canWrite} className="contents">
              <ConnectCard integration={integration} />
            </fieldset>
          </>
        )}
        {canWrite && (
          <div className="flex justify-start">
            <DeleteFieldset integration={integration} />
          </div>
        )}
      </div>
    );
  }

  return (
    <IntegrationConfigWrapper integration={integration}>
      <ProfileCard integration={integration} canWrite={canWrite} />
      <GroupCard integration={integration} canWrite={canWrite} />
      <WelcomeTriggerCard integration={integration} canWrite={canWrite} />
      <SessionTimeoutCard
        integration={integration}
        canWrite={canWrite}
        defaultSeconds={DEFAULT_SESSION_TIMEOUT}
      />
    </IntegrationConfigWrapper>
  );
};

export default LiveChat;
